import _ from 'lodash';
import React, { Component } from 'react';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Modal, FormCheck } from 'react-bootstrap';
import classNames from 'classnames';
import { ClipLoader } from 'react-spinners';
import LoadingPage from '../../../LoadingPage';
import toastr from '../../../toastr';
import modal from '../../typings/Modal';
import Label from '../Label';
import Model from '../../model/ShopKeeper.model';
import { ICreditCorePartLine, ICreditStateMap } from '../../typings/CustomerHistory';

/* This modal displays the part lines on a specific invoice, as well as
   each line's credit status (previously credited, partially credited, etc.).

   It also allows for a 1:1 credit invoice generation.  Each credit invoice must
   match to 1 original invoice, as QM was designed this way.

   In order for this modal to pop, we already have invoice data in the model.
   We may not yet have core return data since it is more lazily loaded, but timing
   wise, it should pretty much always be loaded by now.  We'll check anyways.
*/

interface ICorePart {
  credited: number;
  creditDetails: Array<{
    qty: number;
    creditDate: string;
  }>;
  deleted: boolean;
  dirtyCoresOutstanding: number;
  desc: string;
  lineNumber: string;
  noTrackCores: boolean;
  partNum: string;
  qty: number;
  sellPrice: number;
  useDamageLevels: boolean | undefined;
  damageLevel1: string;
  damageLevel2: string;
  damageLevel3: string;
  coreReturnDaysExceeded: boolean | undefined;
  coreReturnDaysForStockClass: number;
  daysSinceInvoiceDate: number;
}

interface IProps {
  container: Object;
  closeCustomerHistoryModal: () => void;
  hide: () => void;
  model: Model;
  show: boolean;
}

@observer
class CreditInvoiceModal extends Component<IProps, {}> {
  @observable hideCreditHistory = false;
  @observable partSearchInput = '';
  @observable showPartsWithCoresOnly = false;

  handlingChargeHints: number[];
  handlingChargeOptionSelected: string = 'none'; // 'none', 'custom', or index into hints
  handlingChargeCustomValue: string = ''; // % or exact amount

  partSearchDebounced: (evt: any) => void;

  creditStateMap: ICreditStateMap = {
    cashCredit: false,
    creditCardCredit: false,
    comments: ['', '', ''],
    customerPO: '',
    handlingCharge: 0,
    invoiceNum: '',
    creditLines: {},
  };

  creditPpcInput: HTMLInputElement;
  creditPpcPasswordInput: HTMLInputElement;

  constructor(props: IProps) {
    super(props);

    const { activeCreditInvoice } = props.model;

    this.handlingChargeHints = props.model.sysParams.handlingChargeHints
      .split('-')
      .map(hint => parseInt(hint, 10) || 0);

    this.partSearchDebounced = _.debounce(this.partSearch, 500);
    this.partSearchInput = activeCreditInvoice.partSearchFilterSeed;

    this.creditStateMap.invoiceNum = activeCreditInvoice.invoiceNum;
    this.creditStateMap.customerPO = activeCreditInvoice.poNum;
    this.creditStateMap.creditCardCredit = activeCreditInvoice.creditCardSale;
  }

  creditFullInvoiceRemaining = () => {
    const { activeCreditInvoice } = this.props.model;

    // Clear filters - this will get all lines and DOM inputs repainted to the screen
    runInAction(() => {
      (document.getElementById('sk-showOnlyPartsWithCore') as HTMLInputElement).checked = false;
      (document.getElementById('sk-creditPartSearch') as HTMLInputElement).value = '';
      this.partSearchInput = '';
      this.showPartsWithCoresOnly = false;
    });

    // Re-init Credit Lines
    this.creditStateMap.creditLines = {};

    for (const partLine of activeCreditInvoice.partLines) {
      // special case of an unlinked core part (separate core invoice, most)
      if (partLine.corePartUnlinked) {
        const { deleted, dirtyCoresOutstanding, lineNumber, noTrackCores, partNum, sellPrice, useDamageLevels } =
          partLine as ICreditCorePartLine;
        const remainingQtyEligibleForCredit = dirtyCoresOutstanding!;
        const eligibleForCredit = !deleted && !noTrackCores && remainingQtyEligibleForCredit > 0;

        if (eligibleForCredit) {
          this.creditStateMap.creditLines[lineNumber] = {
            partNum,
            creditQty: remainingQtyEligibleForCredit,
            creditPrice: sellPrice,
            returnDirtyCores: false,
          };

          const creditInputEl = document.getElementById(`sk-credit-input-${lineNumber}`) as HTMLInputElement;
          if (creditInputEl) {
            creditInputEl.value = remainingQtyEligibleForCredit.toFixed(0);
          }

          const damageLevelEl = document.getElementById(`sk-credit-damagelevel-${lineNumber}`) as HTMLInputElement;
          if (damageLevelEl) {
            damageLevelEl.value = useDamageLevels ? sellPrice.toFixed(2) : '';
          }
        }
        continue;
      }

      // Otherwise, this is a standard part line (base part in the case of parts with cores),
      // which may have loyalty coupons, cores, and fees attached.

      // block scoped to prevent linter shadowing complaints
      {
        const { coreData: corePart, deleted, lineNumber, partNum, qty, sellPrice } = partLine;
        const credited = partLine.creditQty; // This SHOULD always match the total qtys from the creditDeails items
        const remainingQtyEligibleForCredit = qty - credited;
        const eligibleForCredit = !deleted && remainingQtyEligibleForCredit > 0;

        if (eligibleForCredit) {
          this.creditStateMap.creditLines[lineNumber] = {
            partNum,
            creditQty: remainingQtyEligibleForCredit,
            creditPrice: sellPrice,
            returnDirtyCores: false,

            // These are cached here for summary calculation performance (no need to re-lookup)
            cleanCoreTrade: partLine.coreData
              ? partLine.coreData.sellPrice
              : partLine.separateCleanCoreInvoice
              ? partLine.separateCleanCoreTrade
              : 0,
            feeCharge: partLine.feeData ? partLine.feeData.sellPrice : 0,
            loyaltySavings: partLine.loyaltyCoupons.map(o => ({ couponQty: o.qty, couponPrice: o.sellPrice })),

            // This solution did not take credit line quantity into account and erroneously applied the full loyalty savings.
            // The revised solution above still supports (hypothetical) multiple coupon types per line.
            // partLine.loyaltyCoupons.length > 0
            //   ? partLine.loyaltyCoupons.reduce((acc, coupon) => acc + coupon.qty * coupon.sellPrice, 0)
            //   : 0,
          };

          // React render syncs don't pick up everything we need due to edgecase states of
          // disabled controls and such, so we'll do some direct DOM tweaking here.

          const basePartCreditInputEl = document.getElementById(`sk-credit-input-${lineNumber}`) as HTMLInputElement;
          if (basePartCreditInputEl) {
            basePartCreditInputEl.disabled = false;
            basePartCreditInputEl.value = remainingQtyEligibleForCredit.toFixed(0);
          }
        }

        // Disable Any Core Parts
        if (corePart) {
          const corePartCreditInputEl = document.getElementById(
            `sk-credit-input-${corePart.lineNumber}`
          ) as HTMLInputElement;
          if (corePartCreditInputEl) {
            corePartCreditInputEl.disabled = true;
            corePartCreditInputEl.value = '';
          }
          const corePartDamageLevelEl = document.getElementById(
            `sk-credit-damagelevel-${corePart.lineNumber}`
          ) as HTMLInputElement;
          if (corePartDamageLevelEl) {
            corePartDamageLevelEl.disabled = true;
            corePartDamageLevelEl.value = corePart.useDamageLevels ? corePart.sellPrice.toFixed(2) : '';
          }

          // Okay to bill dirty cores on this pass, though, if given back to customer
          const basePartReturnCoresInputEl = document.getElementById(
            `sk-credit-returncores-${lineNumber}`
          ) as HTMLInputElement;
          if (basePartReturnCoresInputEl) {
            basePartReturnCoresInputEl.disabled = false;
            basePartReturnCoresInputEl.checked = false;
          }
        }
      }
    }

    this.forceUpdate();
  };

  getSummaryData = () => {
    let totalLinesCredited = 0;
    let totalQuantityCredited = 0;
    let dirtyCoresReceived = 0;
    let dirtyCoresReturned = 0;
    let subtotal = 0;
    let handlingCharge = 0;

    // The handling charge % feature specifically should not include:
    // dirty core returns, fees, or clean core prices (unless set to include in PTB2)
    let hcpSubtotal = 0;

    for (const [lineNumber, creditLine] of Object.entries(this.creditStateMap.creditLines)) {
      // If this line worked its way into the state map but was adjusted back to 0 (cleared)
      if (creditLine.creditQty === 0) {
        continue;
      }

      totalLinesCredited++;
      totalQuantityCredited += creditLine.creditQty;

      const dirtyCoreCredit = creditLine.partNum.endsWith('#');

      if (dirtyCoreCredit) {
        dirtyCoresReceived += creditLine.creditQty;
        subtotal += creditLine.creditQty * creditLine.creditPrice;
        // does not apply to handling charge % subtotal

        // Is this a base part credit?
      } else {
        // qty * (trade + fee - loyalty + clean core trade (if present))
        subtotal += creditLine.creditQty * creditLine.creditPrice;
        hcpSubtotal += creditLine.creditQty * creditLine.creditPrice;

        if (creditLine.feeCharge) {
          subtotal += creditLine.creditQty * creditLine.feeCharge;
          // does not apply to handling charge % subtotal
        }

        if (creditLine.loyaltySavings) {
          for (const o of creditLine.loyaltySavings) {
            // It's possible PACCAR gave us fewer redemptions than quantity sold on one of the coupons
            const loyaltySavings = Math.min(creditLine.creditQty, o.couponQty) * o.couponPrice;
            subtotal -= loyaltySavings;
            hcpSubtotal -= loyaltySavings;
          }
        }

        // Clean core trade is included in the summary calculation regardless of separateCleanCoreInvoice.
        // QM will automatically create a secondary credit invoice for the original core part invoice.
        if (creditLine.cleanCoreTrade) {
          subtotal += creditLine.creditQty * creditLine.cleanCoreTrade;

          // only applies to handling charge % subtotal if set in PTB2
          if (this.props.model.sysParams.includeCoreInHandling) {
            hcpSubtotal += creditLine.creditQty * creditLine.cleanCoreTrade;
          }
        }

        // Are we going to return (bill) the dirty cores?
        if (creditLine.returnDirtyCores) {
          dirtyCoresReturned += creditLine.creditQty;

          /* If a reduced (damage-level) value was used when the dirty core was credited,
             QM does not tie that information in here.  The clerk should bill it back manually
             at the correct (reduced) amount rather than use the "Return Dirty Core" option here.
             Per Vic/Kevin, this is an extremely uncommon scenario.  Maybe once a decade.
          */

          // *Should* always be provided by QM, but double check.
          if (creditLine.cleanCoreTrade) {
            subtotal -= creditLine.creditQty * creditLine.cleanCoreTrade;

            // only applies to handling charge % subtotal if set in PTB2
            if (this.props.model.sysParams.includeCoreInHandling) {
              hcpSubtotal -= creditLine.creditQty * creditLine.cleanCoreTrade;
            }
          }
        }
      }
    }

    switch (this.handlingChargeOptionSelected) {
      case 'none':
        // leave handlingCharge = 0
        break;
      case 'custom':
        if (this.handlingChargeCustomValue === '') {
          // leave handlingCharge = 0
        } else if (this.handlingChargeCustomValue.endsWith('%')) {
          // See comments above on handlingChargePercentageSubtotal
          handlingCharge = hcpSubtotal * (parseInt(this.handlingChargeCustomValue, 10) / 100);
        } else {
          handlingCharge = parseFloat(this.handlingChargeCustomValue) || 0;
        }
        break;
      default:
        // See comments above on handlingChargePercentageSubtotal
        handlingCharge = hcpSubtotal * (this.handlingChargeHints[this.handlingChargeOptionSelected] / 100);
        break;
    }

    this.creditStateMap.handlingCharge = handlingCharge;

    return {
      totalLinesCredited,
      totalQuantityCredited,
      dirtyCoresReceived,
      dirtyCoresReturned,
      subtotal,
      handlingCharge,
    };
  };

  // Update selection and re-calc summary table
  handlingChargeChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    this.handlingChargeOptionSelected = evt.target.value;
    this.forceUpdate();
  };

  // Vet the custom handling charge input before updating the summary
  handleCustomHandlingChargeInput = (evt: React.FocusEvent<HTMLInputElement>) => {
    // strip out $ and commmas
    const input = evt.target.value.trim().replace(/[$,]/g, '');

    // emptry string, x%, or xxx / xxx.xx
    if (!(input === '' || /^\d+%$/.test(input) || /^\d+$/.test(input) || /^\d+\.\d\d$/.test(input))) {
      toastr.error('Error', 'Handling Charge Must Follow n% or n.nn');
      this.handlingChargeCustomValue = '';
    } else {
      this.handlingChargeCustomValue = input;
      this.handlingChargeOptionSelected = 'custom';

      // Switch radio option to custom input
      const customInputOptionEl = document.getElementById('sk-customHandlingChargeOption') as HTMLInputElement;
      if (customInputOptionEl) {
        customInputOptionEl.checked = true;
      }
    }

    this.forceUpdate();
  };

  // Strip trailing core/fee indicators
  partSearch = (partSearchInput: string) => {
    const term = partSearchInput.toUpperCase();
    this.partSearchInput = _.includes(['&', '*', '#'], term.slice(-1)) ? term.slice(0, -1) : term;
  };

  renderCoreBankCreditHistory = ({
    creditLineItems,
    corePart,
    isCrediting,
  }: {
    creditLineItems: JSX.Element[];
    corePart: ICorePart;
    isCrediting: boolean;
  }) => {
    // Core Bank Credit History
    if (!this.hideCreditHistory) {
      if (corePart.noTrackCores) {
        creditLineItems.push(
          <tr
            className={classNames({
              'sk-crediLineExtraInfo': true,
              'sk-creditLineCrediting': isCrediting,
            })}
            key={`creditLineCorebankStatusAndCredits-${corePart.lineNumber}`}
          >
            <td colSpan={4} />
            <td colSpan={3} className="sk-previousCreditInfo">
              Dirty Cores Not Tracked For This Part
            </td>
          </tr>
        );
      } else if (corePart.creditDetails.length > 0) {
        creditLineItems.push(
          <tr
            className={classNames({
              'sk-crediLineExtraInfo': true,
              'sk-creditLineCrediting': isCrediting,
            })}
            key={`creditLineCorebankStatusAndCredits-${corePart.lineNumber}`}
          >
            <td colSpan={4} />
            <td colSpan={3} className="sk-previousCreditInfo">
              {corePart.creditDetails.map((creditDetail, i) => (
                <div key={`coreCreditLine-${corePart.lineNumber}-creditDetail-${i}`}>
                  Core Credited {creditDetail.qty} on {creditDetail.creditDate}
                </div>
              ))}
            </td>
          </tr>
        );
      }
    }
  };

  // These are shared by both unlinked & linked core parts (logic is 99% the same)
  renderCoreDamageControls = ({
    corePart,
    value,
    disabled,
  }: {
    corePart: ICorePart;
    value: string | undefined;
    disabled: boolean;
  }) => {
    const coreCreditLineExists = this.creditStateMap.creditLines[corePart.lineNumber];
    return (
      <>
        {corePart.useDamageLevels && (
          <select
            id={`sk-credit-damagelevel-${corePart.lineNumber}`}
            className="sk-damageLevel"
            disabled={disabled}
            // If undefined, defaults to No Damage
            value={value}
            onChange={evt => {
              // Update Credit State Map
              if (coreCreditLineExists) {
                this.creditStateMap.creditLines[corePart.lineNumber].damageLevelInput = evt.target.value;
                this.creditStateMap.creditLines[corePart.lineNumber].creditPrice = parseFloat(evt.target.value) || 0;
              } else {
                this.creditStateMap.creditLines[corePart.lineNumber] = {
                  partNum: corePart.partNum,
                  creditQty: 0,
                  creditPrice: parseFloat(evt.target.value) || 0,
                  returnDirtyCores: false,
                  damageLevelInput: evt.target.value,
                };
              }

              this.creditStateMap.creditLines[corePart.lineNumber].damageLevelForced =
                this.creditStateMap.creditLines[corePart.lineNumber].creditPrice !== corePart.sellPrice;

              this.forceUpdate();
            }}
          >
            <option value={corePart.sellPrice.toFixed(2)}>No Damage</option>
            <option value={corePart.damageLevel1}>Level 1: {corePart.damageLevel1}</option>
            <option value={corePart.damageLevel2}>Level 2: {corePart.damageLevel2}</option>
            <option value={corePart.damageLevel3}>Level 3: {corePart.damageLevel3}</option>
          </select>
        )}
        {!corePart.useDamageLevels && (
          <input
            type="text"
            id={`sk-credit-damagelevel-${corePart.lineNumber}`}
            className={classNames({
              'sk-damageLevelCustomInput': true,
              'sk-damageLevelCustomInputSet':
                coreCreditLineExists && !!this.creditStateMap.creditLines[corePart.lineNumber].damageLevelInput,
            })}
            placeholder="% or Dollar Amount (Damaged)"
            disabled={disabled}
            defaultValue={value}
            onBlur={evt => {
              // Check for valid input (%, NNN or NNN.NN), if invalid, revert to trade if credit line exists
              const input = evt.target.value;
              const invalidInput = !(/^\d+%$/.test(input) || /^\d+$/.test(input) || /^\d+\.\d\d$/.test(input));

              // Don't show error when clearing the field
              if (input !== '' && invalidInput) {
                toastr.error('Error', 'Damage Value Must Follow n% or n.nn');
              }

              if (input === '' || invalidInput) {
                if (coreCreditLineExists) {
                  this.creditStateMap.creditLines[corePart.lineNumber].damageLevelInput = '';
                  this.creditStateMap.creditLines[corePart.lineNumber].creditPrice = corePart.sellPrice;
                  this.creditStateMap.creditLines[corePart.lineNumber].damageLevelForced = false;
                }
              } else {
                // Valid Input, Update Credit State Map
                let damageLevelValue: number;
                if (input.endsWith('%')) {
                  damageLevelValue = corePart.sellPrice * (parseInt(input, 10) / 100);
                } else {
                  damageLevelValue = parseFloat(input) || 0;
                }

                // round to 2 digits in the process
                const damageLevelStr = damageLevelValue.toFixed(2);
                evt.target.value = damageLevelStr;

                // check for damage price > original core trade price (this is invalid)
                if (damageLevelValue > corePart.sellPrice) {
                  toastr.error('Error', 'Damage Price Exceeds Core Trade!  Try again.');

                  if (coreCreditLineExists) {
                    this.creditStateMap.creditLines[corePart.lineNumber].damageLevelInput = '';
                    this.creditStateMap.creditLines[corePart.lineNumber].creditPrice = corePart.sellPrice;
                    this.creditStateMap.creditLines[corePart.lineNumber].damageLevelForced = false;
                  }

                  this.forceUpdate();
                  return;
                }

                if (coreCreditLineExists) {
                  this.creditStateMap.creditLines[corePart.lineNumber].damageLevelInput = damageLevelStr;
                  this.creditStateMap.creditLines[corePart.lineNumber].creditPrice = parseFloat(damageLevelStr) || 0;
                } else {
                  this.creditStateMap.creditLines[corePart.lineNumber] = {
                    partNum: corePart.partNum,
                    creditQty: 0,
                    creditPrice: parseFloat(damageLevelStr) || 0,
                    returnDirtyCores: false,
                    damageLevelInput: damageLevelStr,
                  };
                }

                this.creditStateMap.creditLines[corePart.lineNumber].damageLevelForced =
                  this.creditStateMap.creditLines[corePart.lineNumber].creditPrice !== corePart.sellPrice;
              }

              this.forceUpdate();
            }}
            onKeyPress={evt => {
              if (evt.key === 'Enter') {
                (evt.target as HTMLInputElement).blur();
              }
            }}
          />
        )}
      </>
    );
  };

  // This is the primary credit line item render routine
  renderCreditLineItems = () => {
    const { activeCreditInvoice } = this.props.model;
    const creditLineItems: JSX.Element[] = [];

    for (const partLine of activeCreditInvoice.partLines) {
      // Apply Part Filter
      if (!!this.partSearchInput && !partLine.partNum.includes(this.partSearchInput)) continue;

      // Apply Parts w/ Cores Only Filter
      if (this.showPartsWithCoresOnly && !partLine.coreData && !partLine.corePartUnlinked) continue;

      // Is this a stand alone (unlinked) core part we'd find on dedicated core invoices?
      // If so, the logic is less conflated than a base + core combo scenario, and rendering rules differ.
      if (partLine.corePartUnlinked) {
        const corePartLine = partLine as ICreditCorePartLine;
        const corePart: ICorePart = {
          credited: corePartLine.creditQty,
          creditDetails: corePartLine.creditDetails,
          deleted: corePartLine.deleted,
          dirtyCoresOutstanding: corePartLine.dirtyCoresOutstanding as number,
          desc: corePartLine.desc,
          lineNumber: corePartLine.lineNumber,
          noTrackCores: corePartLine.noTrackCores,
          partNum: corePartLine.partNum,
          qty: corePartLine.qty,
          sellPrice: corePartLine.sellPrice,
          useDamageLevels: corePartLine.useDamageLevels,
          damageLevel1: (corePartLine.trade2 || 0).toFixed(2),
          damageLevel2: (corePartLine.trade3 || 0).toFixed(2),
          damageLevel3: (corePartLine.trade4 || 0).toFixed(2),
          coreReturnDaysExceeded: corePartLine.coreReturnDaysExceeded,
          coreReturnDaysForStockClass: corePartLine.coreReturnDaysForStockClass || -1,
          daysSinceInvoiceDate: corePartLine.daysSinceInvoiceDate || -1,
        };

        // Our only deterministic rubric is consulting the core bank
        const coreEligibleForCredit = !corePart.noTrackCores && !corePart.deleted && corePart.dirtyCoresOutstanding > 0;
        const coreCreditLineExists = this.creditStateMap.creditLines[corePart.lineNumber];
        const corePartIsCrediting =
          this.creditStateMap.creditLines[corePart.lineNumber] &&
          this.creditStateMap.creditLines[corePart.lineNumber].creditQty > 0;

        const y = this.creditStateMap.creditLines;
        const defaultValuesUnlinkedCorePart = {
          corePartCreditQty: y[corePart.lineNumber] ? y[corePart.lineNumber].creditQty || '' : '',
          damageLevelInput: y[corePart.lineNumber] ? y[corePart.lineNumber].damageLevelInput : undefined,
        };

        creditLineItems.push(
          <tr
            className={classNames({
              'sk-creditLineDetails': true,
              'sk-creditLineCrediting': corePartIsCrediting,
            })}
            key={`creditLineBase-${corePart.lineNumber}`}
          >
            <td>
              {coreEligibleForCredit && (
                <input
                  type="text"
                  id={`sk-credit-input-${corePart.lineNumber}`}
                  className="sk-creditQuantity"
                  defaultValue={defaultValuesUnlinkedCorePart.corePartCreditQty}
                  onBlur={evt => {
                    const creditQtyInput = evt.target.value.trim() === '' ? 0 : parseInt(evt.target.value, 10);

                    // Bad input or value out of range (other than 0 to clear)?
                    if (
                      creditQtyInput !== 0 &&
                      (Number.isNaN(creditQtyInput) ||
                        creditQtyInput < 1 ||
                        creditQtyInput > corePart.dirtyCoresOutstanding)
                    ) {
                      // clear out any existing quantity here
                      if (coreCreditLineExists) {
                        this.creditStateMap.creditLines[corePart.lineNumber].creditQty = 0;
                      }

                      evt.target.value = '';
                      toastr.error('Error', `Credit qty must be between 1 and ${corePart.dirtyCoresOutstanding}`);

                      // Valid credit input value or 0-ing out
                    } else {
                      // Update Credit State Map
                      if (coreCreditLineExists) {
                        this.creditStateMap.creditLines[corePart.lineNumber].creditQty = creditQtyInput;
                      } else {
                        this.creditStateMap.creditLines[corePart.lineNumber] = {
                          partNum: corePart.partNum,
                          creditQty: creditQtyInput,
                          creditPrice: corePart.sellPrice,
                          returnDirtyCores: false,
                        };
                      }
                    }
                    this.forceUpdate();
                  }}
                  onKeyPress={evt => {
                    if (evt.key === 'Enter') {
                      (evt.target as HTMLInputElement).blur();
                    }
                  }}
                />
              )}
            </td>
            <td className={classNames({ 'sk-deletedPart': corePart.deleted })}>
              <span title={`${corePart.partNum} (${corePart.desc})`}>
                {corePart.partNum} ({corePart.desc})
              </span>
              {corePart.deleted ? <div>This Part Has Been Deleted!</div> : null}
              {!corePart.deleted && corePart.coreReturnDaysExceeded && (
                <div className="sk-coreReturnDaysExceeded">
                  Return Days Exceeded! {corePart.daysSinceInvoiceDate}
                  {' > '}
                  {corePart.coreReturnDaysForStockClass}
                </div>
              )}
            </td>
            <td>
              {coreEligibleForCredit &&
                this.renderCoreDamageControls({
                  corePart,
                  value: defaultValuesUnlinkedCorePart.damageLevelInput,
                  disabled: false,
                })}
            </td>
            <td>{corePart.sellPrice.toFixed(2)}</td>
            <td>{corePart.qty}</td>
            <td>{corePart.noTrackCores ? '-' : corePart.credited}</td>
            <td>
              {corePart.noTrackCores ? '-' : <span>{corePart.dirtyCoresOutstanding} &nbsp; &nbsp; (Core Bank)</span>}
            </td>
          </tr>
        );

        this.renderCoreBankCreditHistory({ creditLineItems, corePart, isCrediting: corePartIsCrediting });

        // No need to consider fees, loyalty, etc. on clean cores
        continue;
      }

      // Otherwise, this is a standard part line (base part in the case of parts with cores),
      // which may have loyalty coupons, cores, and fees attached.

      const basePartIsCrediting =
        this.creditStateMap.creditLines[partLine.lineNumber] &&
        this.creditStateMap.creditLines[partLine.lineNumber].creditQty > 0;

      const x = this.creditStateMap.creditLines;
      const defaultValues = {
        basePartCreditQty: x[partLine.lineNumber] ? x[partLine.lineNumber].creditQty || '' : '',
        corePartCreditQty:
          partLine.coreData && x[partLine.coreData.lineNumber] ? x[partLine.coreData.lineNumber].creditQty || '' : '',
        returnCoresCheckbox: x[partLine.lineNumber] ? x[partLine.lineNumber].returnDirtyCores : false,
        damageLevelInput:
          partLine.coreData && x[partLine.coreData.lineNumber]
            ? x[partLine.coreData.lineNumber].damageLevelInput
            : undefined,
      };

      // Base part render (important to block scope these constants)
      {
        const { coreData: corePart, desc, deleted, lineNumber, partNum, qty, sellPrice } = partLine;
        const credited = partLine.creditQty; // This SHOULD always match the total qtys from the creditDeails items
        const remainingQtyEligibleForCredit = qty - credited;
        const eligibleForCredit = !deleted && remainingQtyEligibleForCredit > 0;

        creditLineItems.push(
          <tr
            className={classNames({
              'sk-creditLineDetails': true,
              'sk-creditLineCrediting': basePartIsCrediting,
            })}
            key={`creditLineBase-${lineNumber}`}
          >
            <td>
              {eligibleForCredit && (
                <input
                  type="text"
                  id={`sk-credit-input-${lineNumber}`}
                  className="sk-creditQuantity"
                  defaultValue={defaultValues.basePartCreditQty}
                  disabled={defaultValues.corePartCreditQty !== ''}
                  onBlur={evt => {
                    const creditQtyInput = evt.target.value.trim() === '' ? 0 : parseInt(evt.target.value, 10);
                    const creditLineExists = this.creditStateMap.creditLines[lineNumber];
                    const corePartCreditLineExists = corePart && this.creditStateMap.creditLines[corePart.lineNumber];

                    // The following may not exist in the DOM if the base part has no core part associated,
                    // or if the core part previously associated has since been deleted.
                    const corePartCreditInputEl = corePart
                      ? (document.getElementById(`sk-credit-input-${corePart.lineNumber}`) as HTMLInputElement)
                      : null;

                    const corePartDamageLevelEl = corePart
                      ? (document.getElementById(`sk-credit-damagelevel-${corePart.lineNumber}`) as HTMLInputElement)
                      : null;

                    let clearingBasePartInput = false; // bad input or 0

                    // Bad input or value out of range (other than 0 to clear)?
                    if (
                      creditQtyInput !== 0 &&
                      (Number.isNaN(creditQtyInput) ||
                        creditQtyInput < 1 ||
                        creditQtyInput > remainingQtyEligibleForCredit)
                    ) {
                      // clear out any existing quantity here
                      clearingBasePartInput = true;
                      if (creditLineExists) {
                        this.creditStateMap.creditLines[lineNumber].creditQty = 0;
                      }

                      evt.target.value = '';
                      toastr.error('Error', `Credit qty must be between 1 and ${remainingQtyEligibleForCredit}`);

                      // Valid credit input value or 0-ing out
                    } else {
                      // Update Credit State Map
                      if (creditLineExists) {
                        this.creditStateMap.creditLines[lineNumber].creditQty = creditQtyInput;
                      } else {
                        this.creditStateMap.creditLines[lineNumber] = {
                          partNum,
                          creditQty: creditQtyInput,
                          creditPrice: sellPrice,
                          returnDirtyCores: false,

                          // These are cached here for summary calculation performance (no need to re-lookup)
                          cleanCoreTrade: partLine.coreData
                            ? partLine.coreData.sellPrice
                            : partLine.separateCleanCoreInvoice
                            ? partLine.separateCleanCoreTrade
                            : 0,
                          feeCharge: partLine.feeData ? partLine.feeData.sellPrice : 0,
                          loyaltySavings: partLine.loyaltyCoupons.map(o => ({
                            couponQty: o.qty,
                            couponPrice: o.sellPrice,
                          })),
                        };
                      }

                      // Update DOM controls (mutual exclusion in base part / dirty core credit inputs)
                      if (creditQtyInput === 0) {
                        clearingBasePartInput = true;
                      } else {
                        // Valid input on a base part credit negates the option of dirty core credits, if present
                        if (corePartCreditLineExists) {
                          this.creditStateMap.creditLines[corePart!.lineNumber].creditQty = 0;
                        }

                        // Update core part input if it exists
                        if (corePartCreditInputEl) {
                          corePartCreditInputEl.value = '';
                          corePartCreditInputEl.disabled = true;
                        }
                        if (corePartDamageLevelEl) {
                          corePartDamageLevelEl.disabled = true;
                        }
                      }
                    }

                    // If the base part input is cleared, we can re-enable dirty core credits, if present
                    if (clearingBasePartInput) {
                      if (corePartCreditInputEl) {
                        corePartCreditInputEl.disabled = false;
                      }
                      if (corePartDamageLevelEl) {
                        corePartDamageLevelEl.disabled = false;
                      }
                    }

                    this.forceUpdate();
                  }}
                  onKeyPress={evt => {
                    if (evt.key === 'Enter') {
                      (evt.target as HTMLInputElement).blur();
                    }
                  }}
                />
              )}
            </td>
            <td className={classNames({ 'sk-deletedPart': deleted })}>
              <span title={partNum}>{partNum}</span>
              {deleted ? <div>This Part Has Been Deleted!</div> : null}
            </td>
            <td>
              <span title={desc}>{desc}</span>
            </td>
            <td>{sellPrice.toFixed(2)}</td>
            <td>{qty}</td>
            <td>{credited}</td>
            <td>{remainingQtyEligibleForCredit}</td>
          </tr>
        );
      }

      // Primary Part Line Loyalty Info + Credit History
      if (partLine.loyaltyCoupons.length > 0 || partLine.creditDetails.length > 0) {
        const { creditDetails, lineNumber, loyaltyCoupons } = partLine;
        creditLineItems.push(
          <tr
            className={classNames({
              'sk-crediLineExtraInfo': true,
              'sk-creditLineCrediting': basePartIsCrediting,
            })}
            key={`creditLineBase-LoyaltyAndCredits-${lineNumber}`}
          >
            <td />
            <td colSpan={3} className="sk-loyaltyInfo">
              {loyaltyCoupons.map((loyaltyCoupon, i) => (
                <div key={`creditLine-${lineNumber}-loyaltyCoupon-${i}`}>
                  PACCAR Loyalty: {loyaltyCoupon.qty}@{loyaltyCoupon.sellPrice.toFixed(2)} - {loyaltyCoupon.desc}
                </div>
              ))}
            </td>
            <td colSpan={3} className="sk-previousCreditInfo">
              {!this.hideCreditHistory &&
                creditDetails.map((creditDetail, i) => (
                  <div key={`creditLine-${lineNumber}-creditDetail-${i}`}>
                    Credited {creditDetail.qty} on {creditDetail.creditDate}
                  </div>
                ))}
            </td>
          </tr>
        );
      }

      // Primary Part Fees
      if (partLine.feeData) {
        const { desc, lineNumber, sellPrice } = partLine.feeData;
        creditLineItems.push(
          <tr
            className={classNames({
              'sk-creditLineCoreOrFeeInfo': true,
              'sk-creditLineCrediting': basePartIsCrediting,
            })}
            key={`creditLineBaseFee-${lineNumber}`}
          >
            <td />
            <td>FEE</td>
            <td>
              <span title={desc}>{desc}</span>
            </td>
            <td>{sellPrice.toFixed(2)}</td>
            <td />
            <td />
            <td />
          </tr>
        );
      }

      // Core Parts (credits issued directly to cores must be mutually exclusive from the base part)
      if (partLine.coreData) {
        // Be explicit when referencing the base/core parts
        const basePart = {
          deleted: partLine.deleted,
          lineNumber: partLine.lineNumber,
          partNum: partLine.partNum,
          remainingQtyEligibleForCredit: partLine.qty - partLine.creditQty,
          sellPrice: partLine.sellPrice,
        };
        const corePart: ICorePart = {
          credited: partLine.coreData.creditQty,
          creditDetails: partLine.coreData.creditDetails,
          deleted: partLine.coreData.deleted,
          dirtyCoresOutstanding: partLine.coreData.dirtyCoresOutstanding as number,
          desc: partLine.coreData.desc,
          lineNumber: partLine.coreData.lineNumber,
          noTrackCores: partLine.coreData.noTrackCores,
          partNum: partLine.coreData.partNum,
          qty: partLine.coreData.qty,
          sellPrice: partLine.coreData.sellPrice,
          useDamageLevels: partLine.coreData.useDamageLevels,
          damageLevel1: (partLine.coreData.trade2 || 0).toFixed(2),
          damageLevel2: (partLine.coreData.trade3 || 0).toFixed(2),
          damageLevel3: (partLine.coreData.trade4 || 0).toFixed(2),
          coreReturnDaysExceeded: partLine.coreData.coreReturnDaysExceeded,
          coreReturnDaysForStockClass: partLine.coreData.coreReturnDaysForStockClass || -1,
          daysSinceInvoiceDate: partLine.coreData.daysSinceInvoiceDate || -1,
        };

        const basePartCreditLineExists = this.creditStateMap.creditLines[basePart.lineNumber];
        const coreCreditLineExists = this.creditStateMap.creditLines[corePart.lineNumber];

        // Our only deterministic rubric is consulting the core bank
        const coreEligibleForCredit = !corePart.noTrackCores && !corePart.deleted && corePart.dirtyCoresOutstanding > 0;

        const corePartIsCrediting =
          this.creditStateMap.creditLines[corePart.lineNumber] &&
          this.creditStateMap.creditLines[corePart.lineNumber].creditQty > 0;

        // If cores are tracked for this part (meaning dirty cores are returned), we can offer to return dirty
        // cores to the customer, which would have the credit invoice effect of BILLING out the dirty core. (Rare)
        if (!corePart.noTrackCores && !basePart.deleted && basePart.remainingQtyEligibleForCredit > 0) {
          creditLineItems.push(
            <tr
              className={classNames({
                'sk-creditLineCoreOrFeeInfo': true,
                'sk-creditLineCrediting': basePartIsCrediting,
              })}
              key={`creditLineReturnDirtyCoreOption-${basePart.lineNumber}`}
            >
              <td colSpan={7} className="sk-returnDirtyCores">
                <FormCheck inline={true}>
                  <FormCheck.Input
                    type="checkbox"
                    className="sk-useMaterialInput sk-returnDirtyCoresCheckbox"
                    id={`sk-credit-returncores-${basePart.lineNumber}`}
                    defaultChecked={defaultValues.returnCoresCheckbox}
                    disabled={defaultValues.corePartCreditQty !== ''}
                    onChange={(evt: any) => {
                      if (basePartCreditLineExists) {
                        this.creditStateMap.creditLines[basePart.lineNumber].returnDirtyCores = evt.target.checked;
                      } else {
                        // It's possible the user will check off this option before populating a base part credit line
                        this.creditStateMap.creditLines[basePart.lineNumber] = {
                          partNum: basePart.partNum,
                          creditQty: 0,
                          creditPrice: basePart.sellPrice,
                          returnDirtyCores: evt.target.checked,
                        };
                      }
                      this.forceUpdate();
                    }}
                  />
                  <FormCheck.Label htmlFor={`sk-credit-returncores-${basePart.lineNumber}`}>
                    Return Dirty Cores to Customer (applies only to full part credits)
                  </FormCheck.Label>
                </FormCheck>
              </td>
            </tr>
          );
        }

        // Core Part
        creditLineItems.push(
          <tr
            className={classNames({
              'sk-creditLineCoreOrFeeInfo': true,
              'sk-creditLineCrediting': basePartIsCrediting || corePartIsCrediting,
            })}
            key={`creditLineCore-${corePart.lineNumber}`}
          >
            <td />
            <td className={classNames({ 'sk-deletedPart': corePart.deleted })}>
              {coreEligibleForCredit ? (
                <>
                  <input
                    type="text"
                    id={`sk-credit-input-${corePart.lineNumber}`}
                    className="sk-creditQuantity"
                    defaultValue={defaultValues.corePartCreditQty}
                    disabled={defaultValues.basePartCreditQty !== ''}
                    onBlur={evt => {
                      const creditQtyInput = evt.target.value.trim() === '' ? 0 : parseInt(evt.target.value, 10);

                      // The following 2 may not exist in the DOM if the base part has been deleted, so check before using
                      const basePartCreditInputEl = document.getElementById(
                        `sk-credit-input-${basePart.lineNumber}`
                      ) as HTMLInputElement;
                      const basePartReturnCoresInputEl = document.getElementById(
                        `sk-credit-returncores-${basePart.lineNumber}`
                      ) as HTMLInputElement;

                      let clearingCoreInput = false; // bad input or 0

                      // Bad input or value out of range (other than 0 to clear)?
                      if (
                        creditQtyInput !== 0 &&
                        (Number.isNaN(creditQtyInput) ||
                          creditQtyInput < 1 ||
                          creditQtyInput > corePart.dirtyCoresOutstanding)
                      ) {
                        // clear out any existing quantity here
                        clearingCoreInput = true;
                        if (coreCreditLineExists) {
                          this.creditStateMap.creditLines[corePart.lineNumber].creditQty = 0;
                        }

                        evt.target.value = '';
                        toastr.error('Error', `Credit qty must be between 1 and ${corePart.dirtyCoresOutstanding}`);

                        // Valid credit input value or 0-ing out
                      } else {
                        // Update Credit State Map
                        if (coreCreditLineExists) {
                          this.creditStateMap.creditLines[corePart.lineNumber].creditQty = creditQtyInput;
                        } else {
                          this.creditStateMap.creditLines[corePart.lineNumber] = {
                            partNum: corePart.partNum,
                            creditQty: creditQtyInput,
                            creditPrice: corePart.sellPrice,
                            returnDirtyCores: false,
                          };
                        }

                        // Update DOM controls (mutual exclusion in base part / dirty core credit inputs)
                        if (creditQtyInput === 0) {
                          clearingCoreInput = true;
                        } else {
                          // Valid input on a dirty core credit negates the option of base part credits
                          if (basePartCreditLineExists) {
                            this.creditStateMap.creditLines[basePart.lineNumber].creditQty = 0;
                            this.creditStateMap.creditLines[basePart.lineNumber].returnDirtyCores = false;
                          }

                          // Update base part inputs if they exist (edgecase: deleted parts)
                          if (basePartCreditInputEl) {
                            basePartCreditInputEl.value = '';
                            basePartCreditInputEl.disabled = true;
                          }
                          if (basePartReturnCoresInputEl) {
                            basePartReturnCoresInputEl.checked = false;
                            basePartReturnCoresInputEl.disabled = true;
                          }
                        }
                      }

                      // If the credit core input is cleared, we can re-enable the base part for credits
                      if (clearingCoreInput) {
                        if (basePartCreditInputEl) {
                          basePartCreditInputEl.disabled = false;
                        }
                        if (basePartReturnCoresInputEl) {
                          basePartReturnCoresInputEl.disabled = false;
                        }
                      }

                      this.forceUpdate();
                    }}
                    onKeyPress={evt => {
                      if (evt.key === 'Enter') {
                        (evt.target as HTMLInputElement).blur();
                      }
                    }}
                  />
                  <span title={corePart.desc}>CORE (Dirty Credit)</span>
                </>
              ) : (
                <span title={corePart.desc}>CORE</span>
              )}
              {corePart.deleted ? <div>This Part Has Been Deleted!</div> : null}
              {!corePart.deleted && corePart.coreReturnDaysExceeded && (
                <div className="sk-coreReturnDaysExceeded">
                  Return Days Exceeded! {corePart.daysSinceInvoiceDate}
                  {' > '}
                  {corePart.coreReturnDaysForStockClass}
                </div>
              )}
            </td>
            <td>
              {coreEligibleForCredit &&
                this.renderCoreDamageControls({
                  corePart,
                  value: defaultValues.damageLevelInput,
                  disabled: defaultValues.basePartCreditQty !== '',
                })}
            </td>
            <td>{corePart.sellPrice.toFixed(2)}</td>
            <td>{corePart.qty}</td>
            <td>{corePart.noTrackCores ? '-' : corePart.credited}</td>
            <td>
              {corePart.noTrackCores ? '-' : <span>{corePart.dirtyCoresOutstanding} &nbsp; &nbsp; (Core Bank)</span>}
            </td>
          </tr>
        );

        this.renderCoreBankCreditHistory({
          creditLineItems,
          corePart,
          isCrediting: basePartIsCrediting || corePartIsCrediting,
        });
      }

      // Is this a separate core invoice scenario where this is the base part invoice?  Show supplemental core charge info.
      if (partLine.separateCleanCoreInvoice) {
        const creditLineExists = this.creditStateMap.creditLines[partLine.lineNumber];
        const remainingQtyEligibleForCredit = partLine.qty - partLine.creditQty;

        // We don't know if cores are tracked for this part (meaning dirty cores are returned) since core parts are on a
        // separate invoice, so the clerk will have to use this option when approprirate. It offers to return dirty
        // cores to the customer, which would have the credit invoice effect of BILLING out the dirty core. (Rare)
        if (!partLine.deleted && remainingQtyEligibleForCredit > 0) {
          creditLineItems.push(
            <tr
              className={classNames({
                'sk-creditLineCoreOrFeeInfo': true,
                'sk-creditLineCrediting': basePartIsCrediting,
              })}
              key={`creditLineReturnDirtyCoreOption-${partLine.lineNumber}`}
            >
              <td colSpan={7} className="sk-returnDirtyCores">
                <FormCheck inline={true}>
                  <FormCheck.Input
                    type="checkbox"
                    id={`sk-credit-returncores-${partLine.lineNumber}`}
                    className="sk-useMaterialInput sk-returnDirtyCoresCheckbox"
                    defaultChecked={defaultValues.returnCoresCheckbox}
                    onChange={(evt: any) => {
                      if (creditLineExists) {
                        this.creditStateMap.creditLines[partLine.lineNumber].returnDirtyCores = evt.target.checked;
                      } else {
                        // It's possible the user will check off this option before populating a base part credit line
                        this.creditStateMap.creditLines[partLine.lineNumber] = {
                          partNum: partLine.partNum,
                          creditQty: 0,
                          creditPrice: partLine.sellPrice,
                          returnDirtyCores: evt.target.checked,
                          cleanCoreTrade: partLine.separateCleanCoreTrade,
                        };
                      }
                      this.forceUpdate();
                    }}
                  />
                  <FormCheck.Label htmlFor={`sk-credit-returncores-${partLine.lineNumber}`}>
                    Return Dirty Cores to Customer
                  </FormCheck.Label>
                </FormCheck>
              </td>
            </tr>
          );
        }

        creditLineItems.push(
          <tr
            className={classNames({
              'sk-creditLineCoreOrFeeInfo': true,
              'sk-creditLineCrediting': basePartIsCrediting,
            })}
            key={`creditLineCore-${partLine.lineNumber}`}
          >
            <td />
            <td className={classNames({ 'sk-deletedPart': partLine.deleted })}>CORE</td>
            <td>INVOICED SEPARATELY</td>
            <td>{partLine.separateCleanCoreTrade.toFixed(2)}</td>
            <td />
            <td />
            <td />
          </tr>
        );
      }
    }

    return creditLineItems;
  };

  render() {
    const { container, hide, model, show } = this.props;
    const {
      ccPendingCredit,
      poNum: customerPO,
      invoiceDate,
      invoiceNum,
      legacyCreditCardSale,
    } = model.activeCreditInvoice;

    // This option is only used by accounts on charge/credit with the dealership which forced a cash sale variance and paid with cash.
    // The refund can be back to the charge account (default) OR in cash via this option in such a case when cash sale forces were used.
    const cashCreditAvailableForChargeAccount = model.activeCreditInvoice.cashSale;

    // Credit card sales must be processed as credit card returns.  Even on force-cash charge account sales, the only option
    // is credit card return or cash back.  It is not possible to return a CC credit to the charge account on file.
    // Note: Charge accounts which force a cash sale then pay by credit card have creditCardSale = true, cashSale = false
    const creditCardRefund = model.activeCreditInvoice.creditCardSale && model.sysParams.creditCardIntegration;

    // Cash refund always assumed if not also a credit card sale.
    // Note the cashSale flag is ONLY true on charge accounts which used "force cash sale", which is confusing.
    const codCustomer = model.customerParams.customerCashOnly;

    const filteredState = !!this.partSearchInput || this.showPartsWithCoresOnly;
    const summaryData = this.getSummaryData();

    return (
      <Modal
        backdrop="static"
        size="lg"
        container={container as any}
        dialogClassName="sk-creditInvoiceModal"
        onHide={hide as any}
        show={show}
      >
        <Modal.Header>
          <Modal.Title>
            Credit to Invoice {invoiceNum} ({invoiceDate})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {model.coreReturnDaysLazyLoaded ? (
            <>
              <div className="sk-creditInvoiceHeaderControls">
                <div
                  className="sk-actionLink"
                  onClick={() => {
                    modal.open('viewInvoice', { lookupKey: invoiceNum });
                  }}
                >
                  View / Print Original Invoice
                </div>
                <div className="sk-middleCheckboxControls">
                  <div>
                    <FormCheck inline={true}>
                      <FormCheck.Input
                        type="checkbox"
                        id="sk-hideCreditHistory"
                        className="sk-useMaterialInput"
                        onChange={() => (this.hideCreditHistory = !this.hideCreditHistory)}
                      />
                      <FormCheck.Label htmlFor="sk-hideCreditHistory">Hide Credit History</FormCheck.Label>
                    </FormCheck>
                  </div>
                  <div>
                    <FormCheck inline={true}>
                      <FormCheck.Input
                        type="checkbox"
                        id="sk-showOnlyPartsWithCore"
                        className="sk-useMaterialInput"
                        onChange={() => (this.showPartsWithCoresOnly = !this.showPartsWithCoresOnly)}
                      />
                      <FormCheck.Label htmlFor="sk-showOnlyPartsWithCore">Show Only Parts With Cores</FormCheck.Label>
                    </FormCheck>
                  </div>
                </div>
                <div>
                  <input
                    defaultValue={this.partSearchInput}
                    type="text"
                    id="sk-creditPartSearch"
                    placeholder="Search by Part #"
                    onChange={evt => this.partSearchDebounced(evt.target.value)}
                  />
                </div>
              </div>
              <div className="sk-creditInvoiceMain">
                <div className="sk-creditLineItemsContainer">
                  <div className="sk-creditLineItemsStickyHeader">
                    <table className="sk-creditLineItemCols sk-creditLineItemColsHeaders">
                      <tbody>
                        <tr className={filteredState ? 'sk-filteredState' : ''}>
                          <td>
                            <span title="CREDIT QUANTITY">CREDIT</span>
                          </td>
                          <td>
                            <span title="PART NUMBER">PART</span>
                          </td>
                          <td>
                            <span title="DESCRIPTION">DESCRIPTION</span>
                          </td>
                          <td>
                            <span title="STOCK CLASS">PRICE</span>
                          </td>
                          <td>
                            <span title="INVOICED QUANTITY">INVOICED</span>
                          </td>
                          <td>
                            <span title="PREVIOUSLY CREDITED QUANTITY">CREDITED</span>
                          </td>
                          <td>
                            <span title="REMAINING QUANTITY">REMAINING</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="sk-creditLineItems">
                    <table className="sk-creditLineItemCols">
                      <tbody>{this.renderCreditLineItems()}</tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="sk-creditInvoiceAdditionalCreditFields">
                <div className="sk-creditInvoicePOandComments">
                  <Label>Customer PO</Label>
                  <input
                    type="text"
                    defaultValue={customerPO}
                    onBlur={evt => (this.creditStateMap.customerPO = evt.target.value)}
                    onKeyPress={evt => {
                      if (evt.key === 'Enter') {
                        (evt.target as HTMLInputElement).blur();
                      }
                    }}
                  />
                  <div className="sk-creditComments">
                    <Label>Credit Comments:</Label>
                    <input
                      className="sk-useMaterialInput"
                      type="text"
                      maxLength={50}
                      onBlur={evt => (this.creditStateMap.comments[0] = evt.target.value)}
                      onKeyPress={evt => {
                        if (evt.key === 'Enter') {
                          document.getElementById('sk-creditComment2')!.focus();
                        }
                      }}
                    />
                    <br />
                    <input
                      className="sk-useMaterialInput"
                      type="text"
                      maxLength={50}
                      id="sk-creditComment2"
                      onBlur={evt => (this.creditStateMap.comments[1] = evt.target.value)}
                      onKeyPress={evt => {
                        if (evt.key === 'Enter') {
                          document.getElementById('sk-creditComment3')!.focus();
                        }
                      }}
                    />
                    <br />
                    <input
                      className="sk-useMaterialInput"
                      type="text"
                      maxLength={50}
                      id="sk-creditComment3"
                      onBlur={evt => (this.creditStateMap.comments[2] = evt.target.value)}
                    />
                  </div>
                </div>
                <div className="sk-creditInvoiceHandlingCharge">
                  <Label>Handling Charge</Label>
                  <label>
                    <input
                      type="radio"
                      name="sk-creditHandlingChargeItem"
                      value="none"
                      checked={this.handlingChargeOptionSelected === 'none'}
                      onChange={this.handlingChargeChange}
                    />
                    No Charge
                  </label>
                  {this.handlingChargeHints.map((hint, i) => (
                    <React.Fragment key={`sk-hc-${i}`}>
                      <br />
                      <label>
                        <input
                          type="radio"
                          name="sk-creditHandlingChargeItem"
                          value={i}
                          onChange={this.handlingChargeChange}
                        />
                        {hint}%
                      </label>
                    </React.Fragment>
                  ))}
                  <br />
                  <label>
                    <input
                      type="radio"
                      name="sk-creditHandlingChargeItem"
                      id="sk-customHandlingChargeOption"
                      value="custom"
                      onChange={this.handlingChargeChange}
                    />
                    <input
                      type="text"
                      placeholder="X% or Dollar Amount"
                      onBlur={this.handleCustomHandlingChargeInput}
                      onKeyPress={evt => {
                        if (evt.key === 'Enter') {
                          (evt.target as HTMLInputElement).blur();
                        }
                      }}
                    />
                  </label>
                </div>
                <div
                  className={`sk-creditInvoiceSummary ${
                    this.creditStateMap.cashCredit || this.creditStateMap.creditCardCredit
                      ? 'sk-cashCreditSelected'
                      : ''
                  }`}
                >
                  <table>
                    <tbody>
                      <tr>
                        <td>Total Lines Credited:</td>
                        <td>{summaryData.totalLinesCredited}</td>
                      </tr>
                      <tr>
                        <td>Total Quantity Credited:</td>
                        <td>{summaryData.totalQuantityCredited}</td>
                      </tr>
                      <tr>
                        <td>Dirty Cores Received:</td>
                        <td>{summaryData.dirtyCoresReceived}</td>
                      </tr>
                      <tr>
                        <td>Dirty Cores Returned:</td>
                        <td>{summaryData.dirtyCoresReturned}</td>
                      </tr>
                      <tr>
                        <td>Credits Subtotal:</td>
                        <td>${summaryData.subtotal.toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td>Handling Charge:</td>
                        <td>${summaryData.handlingCharge.toFixed(2)}</td>
                      </tr>
                      {cashCreditAvailableForChargeAccount && (
                        <tr className="sk-creditInvoiceAdditionalOption">
                          <td colSpan={2}>
                            <FormCheck inline={true}>
                              <FormCheck.Input
                                type="checkbox"
                                id="cash-credit"
                                className="sk-useMaterialInput"
                                onChange={(evt: any) => {
                                  this.creditStateMap.cashCredit = evt.target.checked;
                                  this.forceUpdate();
                                }}
                              />
                              <FormCheck.Label htmlFor="cash-credit">Cash Refund (Optional)</FormCheck.Label>
                            </FormCheck>
                          </td>
                        </tr>
                      )}
                      {creditCardRefund && (
                        <tr className="sk-creditInvoiceAdditionalOption">
                          <td colSpan={2}>
                            <i className="fad fa-credit-card" /> Credit Card Refund
                          </td>
                        </tr>
                      )}
                      {codCustomer && !creditCardRefund && (
                        <tr className="sk-creditInvoiceAdditionalOption">
                          <td colSpan={2}>Cash Refund</td>
                        </tr>
                      )}
                      {legacyCreditCardSale && (
                        <tr>
                          <td colSpan={2}>
                            <span style={{ color: 'red' }}>Legacy Credit Card Sale</span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Additional PPC Authentication Required For Credit (PW on Clerk PPC or MGR override) */}
              {model.creditPPCAuthRequired && (
                <div className="sk-creditPPCAuth">
                  <div>
                    <Label>Authorization Required</Label>
                    <input
                      type="text"
                      placeholder="PPC"
                      className="sk-creditPPCInput"
                      defaultValue={model.canClerkPPCIssueCredit ? model.ppcParams.ppc : ''}
                      ref={(ref: HTMLInputElement) => (this.creditPpcInput = ref)}
                    />
                    <input
                      type="password"
                      placeholder="Credits Password"
                      className="sk-creditPPCPWInput"
                      ref={(ref: HTMLInputElement) => (this.creditPpcPasswordInput = ref)}
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <LoadingPage />
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="sk-creditInvoiceModalFooter">
            <div>
              {ccPendingCredit ? (
                <div className="sk-ccPendingCreditFound">
                  Pending CC Credit Invoice Found! You must first complete or delete the pending attempt via UPY6.
                </div>
              ) : (
                <Button onClick={this.creditFullInvoiceRemaining} variant="secondary">
                  Credit Full Invoice Remaining
                </Button>
              )}
            </div>
            <div>
              <Button disabled={model.isCreditInvoiceProcessing} onClick={() => hide()} variant="secondary">
                Cancel
              </Button>
              <Button
                variant="success"
                disabled={
                  ccPendingCredit ||
                  legacyCreditCardSale ||
                  !Object.values(this.creditStateMap.creditLines).some(o => o.creditQty > 0)
                }
                onClick={() => {
                  // If the clerk's PPC isn't pre-approved or requires a manager override, collect auth details
                  if (model.creditPPCAuthRequired) {
                    if (this.creditPpcInput.value.trim() === '' || this.creditPpcPasswordInput.value.trim() === '') {
                      toastr.error('Error', 'PPC Credit Auth Details Required');
                      return;
                    }
                  }

                  if (!model.isCreditInvoiceProcessing) {
                    // set credit invoice request in model / process
                    model.lastCreditInvoiceState = model.creditPPCAuthRequired
                      ? {
                          ...this.creditStateMap,
                          creditPPC: this.creditPpcInput.value.trim(),
                          creditPPCPassword: this.creditPpcPasswordInput.value.trim(),
                        }
                      : this.creditStateMap;

                    // this call is re-entrant for printer selection
                    model.processCreditInvoice();
                  }
                }}
              >
                {model.isCreditInvoiceProcessing ? (
                  <>
                    <ClipLoader size={13} color="white" /> PROCESSING
                  </>
                ) : (
                  'PROCESS'
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default CreditInvoiceModal;
